import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { featureRequestKeys } from "@/application/query-keys/feature-requests.keys";
import {
  featureRequestsRepository,
  type CreateFeatureRequestInput,
} from "@/infrastructure/repositories/feature-requests.supabase.repository";
import type { FeatureRequestRow } from "@/infrastructure/supabase/database.types";

export function useFeatureRequests() {
  return useQuery({
    queryKey: featureRequestKeys.list(),
    queryFn: () => featureRequestsRepository.list(),
    staleTime: 30 * 1000,
  });
}

export function useCreateFeatureRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFeatureRequestInput) => featureRequestsRepository.create(input),
    onSuccess: (row) => {
      queryClient.setQueryData<FeatureRequestRow[]>(featureRequestKeys.list(), (old) =>
        old ? [row, ...old] : [row]
      );
    },
  });
}

export function useUpdateFeatureRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<Pick<FeatureRequestRow, "title" | "description" | "type" | "status">>;
    }) => featureRequestsRepository.update(id, patch),
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: featureRequestKeys.list() });
      const prev = queryClient.getQueryData<FeatureRequestRow[]>(featureRequestKeys.list());
      queryClient.setQueryData<FeatureRequestRow[]>(featureRequestKeys.list(), (old) =>
        old ? old.map((r) => (r.id === id ? { ...r, ...patch } : r)) : old
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(featureRequestKeys.list(), ctx.prev);
      console.error("[useUpdateFeatureRequest]", err);
    },
    onSuccess: (row) => {
      queryClient.setQueryData<FeatureRequestRow[]>(featureRequestKeys.list(), (old) =>
        old ? old.map((r) => (r.id === row.id ? row : r)) : old
      );
    },
  });
}

export function useDeleteFeatureRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => featureRequestsRepository.remove(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: featureRequestKeys.list() });
      const prev = queryClient.getQueryData<FeatureRequestRow[]>(featureRequestKeys.list());
      queryClient.setQueryData<FeatureRequestRow[]>(featureRequestKeys.list(), (old) =>
        old ? old.filter((r) => r.id !== id) : old
      );
      return { prev };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(featureRequestKeys.list(), ctx.prev);
      console.error("[useDeleteFeatureRequest]", err);
    },
  });
}
